/**
 * Operating expenses ledger (R1 item 6, 2026-09-01 design doc) — overhead
 * that is not warehouse or product cost (VA pay, software, marketing, card
 * fees). Owner-only: gated by the route (router.tsx), so no per-action
 * role check is needed here. Mirrors FinancePage's layout conventions
 * (header + range controls, Card sections, shimmer skeletons) without
 * pulling in FinancePage's much larger accounting-provenance machinery.
 */
import { useEffect, useMemo, useState } from 'react';
import { Link } from '@tanstack/react-router';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import {
  OperatingExpenseCategory,
  type OperatingExpense,
  type OperatingExpenseRecurrence,
} from '@sourcito/schemas';
import { AppShell } from '../../components/AppShell';
import { Skeleton, SkeletonLine } from '../../components/Skeleton';
import {
  Badge,
  Button,
  Card,
  CardDescription,
  CardHeader,
  CardTitle,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  FormField,
  Input,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../design';
import { ApiCallError } from '../../lib/api';
import { expenseAttestationMonthLabel, formatFinanceDelta } from '../../lib/finance-summary';
import {
  centsToDollarsInput,
  defaultExpenseRange,
  dollarsInputToCents,
  formatCategory,
  groupExpensesByMonth,
  transitionExpenseRange,
  useCreateOperatingExpense,
  useCreateOperatingExpenseRecurrence,
  useDeleteOperatingExpense,
  useDeleteOperatingExpenseRecurrence,
  useOperatingExpenses,
  useUpdateOperatingExpenseRecurrence,
  type ExpenseMonthGroup,
  type ExpenseRange,
} from '../../lib/operating-expenses';

const CATEGORY_OPTIONS = OperatingExpenseCategory.options;

function currentMonthString(): string {
  return new Date().toISOString().slice(0, 7);
}

// ─────────────────────────────────────────────────────────
// Range controls — a small local variant of CompleteMonthRangeControls
// (components/CompleteMonthRangeControls.tsx): same draft-buffering fix
// (Faraz, 2026-09-01 — desktop Firefox renders type="month" as plain text,
// so a partial keystroke must stay visible instead of snapping back), but
// over ExpenseRange's plain 'YYYY-MM' bounds rather than FinancePeriod's
// complete-calendar-month date bounds, and without excluding the current
// in-progress month (D2: this month's expenses are knowable ahead).
// ─────────────────────────────────────────────────────────
function ExpenseRangeControls({
  range,
  error,
  onChange,
}: {
  range: ExpenseRange;
  error: string | null;
  onChange: (field: 'from' | 'to', value: string) => void;
}) {
  const [drafts, setDrafts] = useState(range);
  useEffect(() => {
    setDrafts(range);
  }, [range.from, range.to]);

  const handleChange = (field: 'from' | 'to', value: string) => {
    setDrafts((d) => ({ ...d, [field]: value }));
    onChange(field, value);
  };

  return (
    <div className="flex items-center gap-2 flex-wrap">
      <label className="flex items-center gap-2 text-xs text-fg-tertiary">
        From
        <Input
          type="month"
          inputSize="sm"
          value={drafts.from}
          onChange={(event) => handleChange('from', event.target.value)}
          aria-label="Expenses start month"
          aria-invalid={error ? 'true' : undefined}
        />
      </label>
      <label className="flex items-center gap-2 text-xs text-fg-tertiary">
        To
        <Input
          type="month"
          inputSize="sm"
          value={drafts.to}
          onChange={(event) => handleChange('to', event.target.value)}
          aria-label="Expenses end month"
          aria-invalid={error ? 'true' : undefined}
        />
      </label>
      {error && (
        <p className="basis-full text-xs text-negative" role="alert" aria-live="polite">
          {error}
        </p>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────
// Recurring rules
// ─────────────────────────────────────────────────────────
function RecurringCard({
  recurrences,
  onEnd,
  onDelete,
}: {
  recurrences: readonly OperatingExpenseRecurrence[];
  onEnd: (recurrence: OperatingExpenseRecurrence) => void;
  onDelete: (recurrence: OperatingExpenseRecurrence) => void;
}) {
  return (
    <Card>
      <CardHeader>
        <CardTitle>Recurring</CardTitle>
        <CardDescription>
          Each rule materializes one ledger row every month, through the current month.
        </CardDescription>
      </CardHeader>
      {recurrences.length === 0 ? (
        <p className="text-sm text-fg-secondary">No recurring expenses yet.</p>
      ) : (
        <div className="divide-y divide-border-subtle">
          {recurrences.map((recurrence) => (
            <div
              key={recurrence.id}
              className="flex items-center justify-between gap-3 py-3 flex-wrap"
            >
              <div>
                <p className="text-sm font-medium text-fg-primary">{recurrence.description}</p>
                <p className="text-xs text-fg-tertiary mt-0.5">
                  {formatCategory(recurrence.category)} ·{' '}
                  {formatFinanceDelta(recurrence.amountCents, recurrence.currency)} · since{' '}
                  {expenseAttestationMonthLabel(recurrence.startMonth)} ·{' '}
                  {recurrence.endMonth
                    ? `until ${expenseAttestationMonthLabel(recurrence.endMonth)}`
                    : 'ongoing'}
                </p>
              </div>
              <div className="flex items-center gap-2">
                {recurrence.endMonth == null && (
                  <Button variant="ghost" size="sm" onClick={() => onEnd(recurrence)}>
                    End
                  </Button>
                )}
                <Button variant="ghost" size="sm" onClick={() => onDelete(recurrence)}>
                  Delete
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}
    </Card>
  );
}

// ─────────────────────────────────────────────────────────
// Add expense form
// ─────────────────────────────────────────────────────────
const ExpenseFormValues = z
  .object({
    description: z
      .string()
      .trim()
      .min(1, 'Description is required')
      .max(200, 'Keep it under 200 characters'),
    category: OperatingExpenseCategory,
    amount: z.string().min(1, 'Amount is required'),
    month: z.string().regex(/^\d{4}-(0[1-9]|1[0-2])$/, 'Select a month'),
    repeatsMonthly: z.boolean(),
  })
  .refine((v) => dollarsInputToCents(v.amount) !== null, {
    message: 'Enter a positive dollar amount',
    path: ['amount'],
  });
type ExpenseFormValues = z.infer<typeof ExpenseFormValues>;

function emptyFormValues(): ExpenseFormValues {
  return {
    description: '',
    category: 'other',
    amount: '',
    month: currentMonthString(),
    repeatsMonthly: false,
  };
}

function AddExpenseForm({
  createExpense,
  createRecurrence,
}: {
  createExpense: ReturnType<typeof useCreateOperatingExpense>;
  createRecurrence: ReturnType<typeof useCreateOperatingExpenseRecurrence>;
}) {
  const {
    handleSubmit,
    watch,
    setValue,
    reset,
    formState: { errors, isSubmitting },
  } = useForm<ExpenseFormValues>({
    resolver: zodResolver(ExpenseFormValues),
    defaultValues: emptyFormValues(),
  });
  const [justAdded, setJustAdded] = useState(false);

  const description = watch('description');
  const amount = watch('amount');
  const month = watch('month');
  const repeatsMonthly = watch('repeatsMonthly');
  const category = watch('category');

  const activeMutation = createRecurrence.isError
    ? createRecurrence
    : createExpense.isError
      ? createExpense
      : null;
  const errorMessage =
    activeMutation?.error instanceof ApiCallError
      ? activeMutation.error.message
      : activeMutation?.error
        ? ((activeMutation.error as Error).message ?? 'Could not add the expense.')
        : null;

  return (
    <Card>
      <CardHeader>
        <CardTitle>Add expense</CardTitle>
        <CardDescription>A one-off cost, or a rule that repeats every month.</CardDescription>
      </CardHeader>
      <form
        onSubmit={handleSubmit(async (values) => {
          setJustAdded(false);
          const cents = dollarsInputToCents(values.amount);
          if (cents == null) return;
          try {
            if (values.repeatsMonthly) {
              await createRecurrence.mutateAsync({
                category: values.category,
                amountCents: cents,
                description: values.description,
                startMonth: values.month,
              });
            } else {
              await createExpense.mutateAsync({
                category: values.category,
                amountCents: cents,
                month: values.month,
                description: values.description,
              });
            }
            reset(emptyFormValues());
            setJustAdded(true);
          } catch {
            // surfaced via createExpense.isError / createRecurrence.isError
          }
        })}
        className="space-y-5"
      >
        <FormField
          label="Description"
          htmlFor="description"
          error={errors.description?.message}
          required
        >
          <Input
            id="description"
            type="text"
            placeholder="VA monthly pay"
            value={description}
            onChange={(event) => setValue('description', event.target.value)}
            aria-invalid={errors.description ? true : undefined}
          />
        </FormField>

        <FormField label="Category" htmlFor="category" required>
          <Select
            value={category}
            onValueChange={(value) => setValue('category', value as ExpenseFormValues['category'])}
          >
            <SelectTrigger id="category">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {CATEGORY_OPTIONS.map((option) => (
                <SelectItem key={option} value={option}>
                  {formatCategory(option)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </FormField>

        <FormField
          label="Amount"
          htmlFor="amount"
          error={errors.amount?.message}
          required
          helper="USD"
        >
          <Input
            id="amount"
            type="text"
            inputMode="decimal"
            placeholder="650.00"
            value={amount}
            onChange={(event) => setValue('amount', event.target.value)}
            aria-invalid={errors.amount ? true : undefined}
          />
        </FormField>

        <FormField
          label={repeatsMonthly ? 'Starting month' : 'Month'}
          htmlFor="month"
          error={errors.month?.message}
          required
        >
          <Input
            id="month"
            type="month"
            value={month}
            onChange={(event) => setValue('month', event.target.value)}
            aria-invalid={errors.month ? true : undefined}
          />
        </FormField>

        <div className="flex items-center justify-between gap-3 rounded-md bg-warning/[0.06] border border-warning/20 px-3 py-2.5">
          <div>
            <label htmlFor="repeatsMonthly" className="text-sm font-medium text-fg-primary">
              Repeats monthly
            </label>
            <p className="text-xs text-fg-tertiary mt-0.5">
              Materializes a row every month from the starting month through the current month.
            </p>
          </div>
          <input
            id="repeatsMonthly"
            type="checkbox"
            checked={repeatsMonthly}
            onChange={(event) => setValue('repeatsMonthly', event.target.checked)}
            className="size-5 cursor-pointer"
          />
        </div>

        {errorMessage && (
          <div className="rounded-sm bg-negative/[0.12] border border-negative/30 px-3 py-2">
            <p className="text-sm text-negative">{errorMessage}</p>
          </div>
        )}
        {!errorMessage && justAdded && (
          <div className="rounded-sm bg-positive/[0.06] border border-positive/30 px-3 py-2">
            <p className="text-sm text-positive">Added.</p>
          </div>
        )}

        <Button
          type="submit"
          loading={isSubmitting || createExpense.isPending || createRecurrence.isPending}
        >
          Add expense
        </Button>
      </form>
    </Card>
  );
}

// ─────────────────────────────────────────────────────────
// Ledger
// ─────────────────────────────────────────────────────────
function LedgerSection({
  groups,
  onDelete,
}: {
  groups: ExpenseMonthGroup[];
  onDelete: (expense: OperatingExpense) => void;
}) {
  return (
    <section aria-labelledby="expenses-ledger-heading">
      <div className="mb-3">
        <h2 id="expenses-ledger-heading" className="text-lg font-medium text-fg-primary">
          Ledger
        </h2>
        <p className="text-sm text-fg-secondary mt-1">Grouped by month, newest first.</p>
      </div>
      {groups.length === 0 ? (
        <Card>
          <p className="text-sm text-fg-secondary">No expenses in this range.</p>
          <p className="text-xs text-fg-tertiary mt-1">
            Add a one-off expense or a monthly recurrence above.
          </p>
        </Card>
      ) : (
        <div className="space-y-6">
          {groups.map((group) => (
            <div key={group.month}>
              <div className="flex items-center justify-between gap-3 rounded-md bg-warning/[0.06] border border-warning/20 px-3 py-2">
                <p className="text-sm font-medium text-fg-primary">
                  {expenseAttestationMonthLabel(group.month)}
                </p>
                <p className="text-sm tabular-nums text-fg-primary">
                  {formatFinanceDelta(group.subtotalCents, 'USD')}
                </p>
              </div>
              <div className="divide-y divide-border-subtle">
                {group.expenses.map((expense) => (
                  <div
                    key={expense.id}
                    className="flex items-center justify-between gap-3 py-3 flex-wrap"
                  >
                    <div className="flex items-center gap-2 flex-wrap min-w-0">
                      <span className="text-sm text-fg-primary">{expense.description}</span>
                      <span className="text-xs text-fg-tertiary">
                        {formatCategory(expense.category)}
                      </span>
                      {expense.recurrenceId != null && <Badge variant="neutral">Recurring</Badge>}
                    </div>
                    <div className="flex items-center gap-3">
                      <span className="text-sm tabular-nums text-fg-primary">
                        {formatFinanceDelta(expense.amountCents, expense.currency)}
                      </span>
                      <Button variant="ghost" size="sm" onClick={() => onDelete(expense)}>
                        Delete
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </section>
  );
}

// ─────────────────────────────────────────────────────────
// Confirm dialog — shared by End recurrence / Delete recurrence / Delete
// expense, one pending action at a time (pattern mirrors FinancePage's
// WarehouseExpenseAttestationSection).
// ─────────────────────────────────────────────────────────
type PendingAction =
  | { kind: 'end-recurrence'; id: string; label: string }
  | { kind: 'delete-recurrence'; id: string; label: string }
  | { kind: 'delete-expense'; id: string; label: string };

function pendingDialogCopy(pending: PendingAction): {
  title: string;
  body: string;
  confirmLabel: string;
} {
  if (pending.kind === 'end-recurrence') {
    return {
      title: `End "${pending.label}"?`,
      body: 'No further rows are materialized for this recurrence after the current month. Rows already booked stay in the ledger.',
      confirmLabel: 'End',
    };
  }
  if (pending.kind === 'delete-recurrence') {
    return {
      title: `Delete "${pending.label}"?`,
      body: 'Removes the recurring rule. Rows already booked stay in the ledger as history.',
      confirmLabel: 'Delete',
    };
  }
  return {
    title: `Delete "${pending.label}"?`,
    body: 'This ledger row is removed. This cannot be undone.',
    confirmLabel: 'Delete',
  };
}

// ─────────────────────────────────────────────────────────
// Page
// ─────────────────────────────────────────────────────────
export function ExpensesPage() {
  const [range, setRange] = useState<ExpenseRange>(() => defaultExpenseRange());
  const [rangeError, setRangeError] = useState<string | null>(null);
  const query = useOperatingExpenses(range);

  const createExpense = useCreateOperatingExpense();
  const createRecurrence = useCreateOperatingExpenseRecurrence();
  const updateRecurrence = useUpdateOperatingExpenseRecurrence();
  const deleteRecurrence = useDeleteOperatingExpenseRecurrence();
  const deleteExpense = useDeleteOperatingExpense();

  const [pending, setPending] = useState<PendingAction | null>(null);
  const [pendingSubmitting, setPendingSubmitting] = useState(false);
  const [pendingError, setPendingError] = useState<string | null>(null);

  const groups = useMemo(() => groupExpensesByMonth(query.data?.expenses ?? []), [query.data]);

  function closePending() {
    if (pendingSubmitting) return;
    setPending(null);
    setPendingError(null);
  }

  async function confirmPending() {
    if (!pending) return;
    setPendingSubmitting(true);
    setPendingError(null);
    try {
      if (pending.kind === 'end-recurrence') {
        await updateRecurrence.mutateAsync({
          id: pending.id,
          body: { endMonth: currentMonthString() },
        });
      } else if (pending.kind === 'delete-recurrence') {
        await deleteRecurrence.mutateAsync(pending.id);
      } else {
        await deleteExpense.mutateAsync(pending.id);
      }
      setPending(null);
    } catch (err) {
      setPendingError(err instanceof Error ? err.message : 'The request failed.');
    } finally {
      setPendingSubmitting(false);
    }
  }

  const dialogCopy = pending ? pendingDialogCopy(pending) : null;

  return (
    <AppShell>
      <header className="border-b border-border-subtle">
        <div className="max-w-6xl mx-auto px-6 sm:px-8 py-6">
          <div className="flex items-start justify-between gap-5 flex-wrap">
            <div>
              <p className="text-xs uppercase tracking-[0.18em] text-fg-tertiary">EXPENSES</p>
              <h1 className="font-display text-4xl sm:text-[44px] font-medium leading-[1.08] tracking-tight mt-3">
                Overhead, entered once.
              </h1>
              <p className="text-sm text-fg-secondary mt-3 max-w-2xl">
                Costs that are not warehouse or product cost — VA pay, software, marketing, card
                fees. For the full P&amp;L, see{' '}
                <Link to="/app/finance" className="text-accent-fg hover:underline">
                  Finance
                </Link>
                .
              </p>
            </div>
            <ExpenseRangeControls
              range={range}
              error={rangeError}
              onChange={(field, value) => {
                const next = transitionExpenseRange(range, field, value);
                setRangeError(next.error);
                if (!next.error) setRange(next.range);
              }}
            />
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-6 sm:px-8 py-8 space-y-8">
        {query.isLoading && (
          <div className="space-y-8" aria-hidden="true">
            <Card>
              <div className="space-y-3">
                <SkeletonLine className="h-4" />
                <SkeletonLine className="h-4" />
                <SkeletonLine className="h-4" />
              </div>
            </Card>
            <Card>
              <div className="space-y-3">
                <SkeletonLine className="h-4" />
                <SkeletonLine className="h-4" />
              </div>
            </Card>
            <Card>
              <div className="space-y-3">
                <Skeleton className="h-4 w-40" />
                <SkeletonLine className="h-4" />
                <SkeletonLine className="h-4" />
                <SkeletonLine className="h-4" />
              </div>
            </Card>
          </div>
        )}

        {query.isError && (
          <Card>
            <p className="text-sm text-negative">
              Could not load expenses:{' '}
              {query.error instanceof Error ? query.error.message : 'request failed'}
            </p>
          </Card>
        )}

        {query.data && (
          <>
            <RecurringCard
              recurrences={query.data.recurrences}
              onEnd={(recurrence) =>
                setPending({
                  kind: 'end-recurrence',
                  id: recurrence.id,
                  label: recurrence.description,
                })
              }
              onDelete={(recurrence) =>
                setPending({
                  kind: 'delete-recurrence',
                  id: recurrence.id,
                  label: recurrence.description,
                })
              }
            />

            <AddExpenseForm createExpense={createExpense} createRecurrence={createRecurrence} />

            <LedgerSection
              groups={groups}
              onDelete={(expense) =>
                setPending({
                  kind: 'delete-expense',
                  id: expense.id,
                  label: expense.description,
                })
              }
            />
          </>
        )}
      </main>

      <Dialog open={pending != null} onOpenChange={(open) => !open && closePending()}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{dialogCopy?.title}</DialogTitle>
            <DialogDescription>{dialogCopy?.body}</DialogDescription>
          </DialogHeader>
          {pendingError && <p className="text-sm text-negative">{pendingError}</p>}
          <DialogFooter>
            <Button variant="ghost" size="sm" disabled={pendingSubmitting} onClick={closePending}>
              Cancel
            </Button>
            <Button size="sm" disabled={pendingSubmitting} onClick={() => void confirmPending()}>
              {pendingSubmitting ? 'Working…' : (dialogCopy?.confirmLabel ?? 'Confirm')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </AppShell>
  );
}
